import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { BackgroundTask } from '@capawesome/capacitor-background-task'
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch'
import { buildBackgroundFetchConfig } from './offlineQueue'
import { OfflineQueueNative } from './offlineQueueNativePlugin'
import { useQueue } from './useQueue'
import {
  shouldDrainOnFetch,
  type OfflineQueueBackgroundDeps,
} from './offlineQueueBackground'

export function createOfflineQueueBackgroundDeps (): OfflineQueueBackgroundDeps {
  const platform = Capacitor.getPlatform()
  const nativePluginAvailable = platform === 'android'
    && Capacitor.isPluginAvailable('OfflineQueueNative')

  if (nativePluginAvailable) {
    void OfflineQueueNative.register()
  }

  return {
    getPlatform: () => platform,
    drainOnFetch: shouldDrainOnFetch(platform, nativePluginAvailable),
    fetchConfig: buildBackgroundFetchConfig(BackgroundFetch.NETWORK_TYPE_ANY),
    configureFetch: Capacitor.isPluginAvailable('BackgroundFetch')
      ? (config, onEvent, onTimeout) => BackgroundFetch.configure(config, onEvent, onTimeout)
      : async () => undefined,
    finishFetch: (taskId) => {
      if (Capacitor.isPluginAvailable('BackgroundFetch')) {
        BackgroundFetch.finish(taskId)
      }
    },
    addAppStateListener: async (handler) => {
      await App.addListener('appStateChange', ({ isActive }) => {
        void handler({ isActive })
      })
    },
    runWhileBackgrounded: async (work) => {
      if (!Capacitor.isPluginAvailable('BackgroundTask')) {
        await work()
        return
      }
      const holder: { taskId: string } = { taskId: '' }
      let finished = false
      holder.taskId = await BackgroundTask.beforeExit(async () => {
        try {
          await work()
        } finally {
          if (holder.taskId) {
            BackgroundTask.finish({ taskId: holder.taskId })
            finished = true
          }
        }
      })
      if (!finished && holder.taskId) {
        BackgroundTask.finish({ taskId: holder.taskId })
      }
    },
    drain: async () => {
      await useQueue().work({ silent: true })
    },
    drainNative: nativePluginAvailable
      ? () => OfflineQueueNative.drain()
      : undefined,
  }
}
